/**
 * The seam between the session-recording UI and the device recorder.
 *
 * The UI (record card, REC bar, consent flow) only talks to a
 * {@link SessionAudioRecorder}; the native implementation (Capacitor
 * `SessionRecorder` plugin: local 60 s AAC segments, joined on Finish) is
 * registered at app start via `registerSessionAudioRecorder`. Until then — and
 * on the web — the unavailable recorder answers, so the UI can explain that
 * recording needs the phone/tablet app.
 *
 * Every event is content-free (no audio, no transcript).
 */

export type RecorderCaptureState = 'idle' | 'starting' | 'recording' | 'paused' | 'stopped'

export type RecorderPauseReason = 'private_moment' | 'someone_joined'

/** Why the device paused capture on its own. */
export type RecorderInterruptionReason =
  | 'phone_call'
  | 'ascend_call'
  | 'system_interruption'
  | 'route_change'
  | 'low_storage'
  | 'mic_lost'

export interface RecorderSnapshot {
  state: RecorderCaptureState
  recordingId: string | null
  /** Epoch ms capture started (0 when idle). */
  startedAtMs: number
  /** Audio captured so far, excluding pauses. */
  elapsedAudioSec: number
  /** Set while paused by the provider. */
  pauseReason: RecorderPauseReason | null
  /** Set while paused by the device (call, Siri, mic lost…). */
  interruption: RecorderInterruptionReason | null
  capReached: boolean
  lastError: string | null
}

export const IDLE_RECORDER_SNAPSHOT: RecorderSnapshot = {
  state: 'idle',
  recordingId: null,
  startedAtMs: 0,
  elapsedAudioSec: 0,
  pauseReason: null,
  interruption: null,
  capReached: false,
  lastError: null,
}

export type RecorderEvent =
  | { type: 'state'; snapshot: RecorderSnapshot }
  /** Input level for the mic meter, at most ~4 per second. `level` is 0..1. */
  | { type: 'level'; level: number }
  | { type: 'interrupted'; reason: RecorderInterruptionReason; atMs: number }
  | { type: 'resumed'; atMs: number }
  /** Hit the 100-minute cap: capture stopped; finish (upload) as normal. */
  | { type: 'capReached'; atMs: number }
  | { type: 'error'; code: string }

export type RecorderReadiness =
  | { ok: true }
  | {
      ok: false
      reason: 'unsupported' | 'mic_permission' | 'low_storage' | 'call_active' | 'busy'
      /** Short, user-safe explanation. */
      message: string
    }

export interface RecorderFinishResult {
  recordingId: string
  /** Audio seconds captured (excludes pauses). */
  durationSec: number
}

/** A capture left on the device after the app was killed mid-recording. */
export interface OrphanRecording {
  recordingId: string
  startedAtMs: number
  elapsedAudioSec: number
}

export interface SessionAudioRecorder {
  readonly kind: 'native' | 'unavailable'
  /** Synchronous capability check (native shell with the recorder plugin). */
  isSupported(): boolean
  /** Mic permission, free storage, no active call, nothing else recording. */
  checkReadiness(): Promise<RecorderReadiness>
  /** Start capture for a recording the server already created (consent stored). */
  start(input: { recordingId: string; maxSeconds: number }): Promise<void>
  pause(reason: RecorderPauseReason): Promise<void>
  resume(): Promise<void>
  /** Stop capture and close the last segment. Audio stays on the device until uploaded. */
  stop(): Promise<RecorderFinishResult>
  /** Delete the local audio for a recording (discard / consent withdrawn / after upload confirmed). */
  discardLocal(recordingId: string): Promise<void>
  status(): Promise<RecorderSnapshot>
  /** Recordings interrupted by an app kill that can still be finished or discarded. */
  findOrphans(): Promise<OrphanRecording[]>
  subscribe(listener: (event: RecorderEvent) => void): () => void
}

const UNSUPPORTED_MESSAGE =
  'Recording a whole session works in the Ascend app on your phone or tablet.'

/** Web / not-yet-registered fallback: reports unsupported, never records. */
export const unavailableSessionAudioRecorder: SessionAudioRecorder = {
  kind: 'unavailable',
  isSupported: () => false,
  checkReadiness: async () => ({ ok: false, reason: 'unsupported', message: UNSUPPORTED_MESSAGE }),
  start: async () => {
    throw new Error(UNSUPPORTED_MESSAGE)
  },
  pause: async () => undefined,
  resume: async () => undefined,
  stop: async () => {
    throw new Error(UNSUPPORTED_MESSAGE)
  },
  discardLocal: async () => undefined,
  status: async () => IDLE_RECORDER_SNAPSHOT,
  findOrphans: async () => [],
  subscribe: () => () => undefined,
}
